// PATH: src/components/DocumentUpload.tsx — sélecteur de pièce jointe (élève).
//
// Variante « fichier » de PhotoUpload : au lieu d'un aperçu en avatar, un bouton
// de sélection qui envoie le fichier au backend TilmiSchool (relais authentifié
// vers tc_upload) et remonte l'URL publique via onChange. Pensé pour un
// Form.Item : `value` = l'URL courante, `onChange` = la nouvelle URL.
import React, { useState } from "react";
import { Button, Space, Typography, Upload, message } from "antd";
import { CheckCircleFilled, LoadingOutlined, PaperClipOutlined } from "@ant-design/icons";
import { useSchoolApi } from "../api/school";
import { COLORS } from "../theme";

// Doit rester aligné sur la liste blanche et la limite du backend
// (hdl_school/upload.go).
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const MAX_BYTES = 2 * 1024 * 1024;

const DocumentUpload: React.FC<{
  value?: string;
  // Le nom du fichier retenu remonte aussi : la modale s'en sert pour
  // pré-remplir le libellé quand il est laissé vide.
  onChange?: (url: string) => void;
  onFileName?: (name: string) => void;
}> = ({ value, onChange, onFileName }) => {
  const api = useSchoolApi();
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string>();

  // Contrôle côté client AVANT l'envoi : rejet immédiat, sans aller-retour
  // réseau. Le backend revalide de toute façon (le client ne protège rien).
  const beforeUpload = (file: File): boolean => {
    if (!ACCEPT.split(",").includes(file.type)) {
      message.error("Format non autorisé : PDF, JPEG, PNG ou WebP");
      return false;
    }
    if (file.size > MAX_BYTES) {
      message.error("Fichier trop lourd : 2 Mo maximum");
      return false;
    }
    setUploading(true);
    api.uploadDocument(file)
      .then((url) => {
        onChange?.(url);
        setFileName(file.name);
        onFileName?.(file.name);
        // « Envoyé », pas « joint » : à ce stade le fichier n'est que dans le
        // stockage. Le rattachement à l'élève (ou au document d'établissement)
        // demande l'enregistrement du formulaire.
        message.success("Fichier envoyé");
      })
      .catch((err) => message.error(err?.response?.data?.error || "Envoi impossible"))
      .finally(() => setUploading(false));
    // false : on gère l'envoi nous-mêmes, antd ne poste rien.
    return false;
  };

  return (
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      <Upload accept={ACCEPT} showUploadList={false} beforeUpload={beforeUpload} maxCount={1}>
        <Button
          icon={uploading ? <LoadingOutlined /> : <PaperClipOutlined />}
          loading={uploading}
        >
          {value ? "Remplacer le fichier" : "Choisir un fichier"}
        </Button>
      </Upload>
      {value && (
        // Confirme visuellement que le fichier est bien monté : sans cela, une
        // sélection réussie ne laisserait aucune trace à l'écran.
        <Typography.Text style={{ fontSize: 13 }}>
          <CheckCircleFilled style={{ color: COLORS.accent, marginRight: 6 }} />
          {fileName || "Fichier envoyé"}
        </Typography.Text>
      )}
    </Space>
  );
};

export default DocumentUpload;
